import React, { Component } from 'react'
import { PropTypes } from 'prop-types'
import { withStyles, Grid, TextField } from '@material-ui/core'
import { requestInfo } from '../services/api/backend'
import Button from './ButtonMaterial'
import pick from 'lodash.pick'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { showFlashMessage, showFlashMessageOnUnexpectedError } from 'actions'
import isempty from 'lodash.isempty'
import Loader from './Loader'
import { isUserLogged, trackEvent } from '../utils'
import { EMAIL_REGEX, PHONE_REGEX } from '../constants'

const styles = theme => ({
  requestFormDesktop: {
    [theme.breakpoints.up('md')]: {
      backgroundColor: '#ffd200',
      minWidth: '325px',
      padding: '45px 32px',
      height: '100%',
    },
  },
  requestForm: {
    backgroundColor: '#ffd200',
    height: '100%',
    [theme.breakpoints.down('md')]: {
      minWidth: '250px',
    },
    [theme.breakpoints.down('sm')]: {
      bottom: '52px',
      left: '0',
      height: 'auto',
      width: '100%',
      padding: '20px 29px 31px',
    },
  },
  requestInfoButton: {
    [theme.breakpoints.down('sm')]: {
      borderRadius: '0',
      width: '100%',
    },
  },
  inputElement: {
    fontSize: '15px',
    lineHeight: '1.33',
    color: '#404552',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    border: '0',
    padding: '12px',
    boxSizing: 'border-box',
    height: '44px',
    '&:disabled': {
      backgroundColor: 'rgb(211,211,211)',
    },
  },
  formField: {
    width: '100%',
    marginBottom: '12px',
    '& input:-ms-input-placeholder': {
      opacity: '1',
    },
  },
  multiline: {
    height: '108px',
  },
  bootstrapRoot: {
    padding: 0,
    'label + &': {
      marginTop: theme.spacing.unit * 3,
    },
  },
  bootstrapFormLabel: {
    fontSize: '18px',
  },
  closeLink: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
    float: 'right',
    color: '#333645',
    marginBottom: '20px',
  },
})

const formFields = ['firstName', 'lastName', 'phone', 'email', 'message']
const userFields = ['firstName', 'lastName', 'phone', 'email']

class RequestForm extends Component {
  constructor(props) {
    super(props)
    this.state = userFields.reduce((output, field) => ({ ...output, [field]: '' }), {
      errors: {},
      message: 'I am interested in speaking with you about the property you have listed.',
      showCloseButton: false,
      loading: false,
      loggedIn: false,
    })
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  componentDidMount() {
    this.setState({ loggedIn: isUserLogged(this.props.user) })
    const reducer = (data, field) => {
      if (this.props.user.personalData.get(field)) {
        data[field] = this.props.user.personalData.get(field)
      }
      return data
    }
    isUserLogged(this.props.user) && this.setState(userFields.reduce(reducer, {}))
  }

  handleSubmit = async event => {
    event.preventDefault()
    if (this.isValid()) {
      this.loadingState()
      trackEvent({ category: 'Request Info', action: 'Requested info for property id ' + this.props.propertyID })
      try {
        let dataToBeUpdated = pick(this.state, formFields)

        for (var key in dataToBeUpdated) {
          dataToBeUpdated[key] = dataToBeUpdated[key].trim()
        }
        dataToBeUpdated = { ...dataToBeUpdated, propertyId: this.props.propertyID }

        const response = await requestInfo(dataToBeUpdated, isUserLogged(this.props.user) ? this.props.user.token : null)

        if (isempty(response.errors)) {
          this.loadingState()
          this.props.showFlashMessage(null, `Your request has been sent successfully!`)
        } else {
          this.loadingState()
          this.props.showFlashMessage(true, this.submissionError(response.errors))
        }
      } catch (error) {
        this.loadingState()
        this.props.showFlashMessageOnUnexpectedError(
          error,
          'There has been an unexpected error. Please try again. If the error persists, please contact help@dealvestors.com'
        )
      }
    }
  }

  submissionError(error) {
    const errType = error[0].split(':')
    let errMsg = ''
    switch (errType[0]) {
      case 'DUPLICATE_VALUE':
        errMsg = 'You have already requested the information for this property.'
        break
      case 'STRING_TOO_LONG':
        errMsg = errType[1] + ' is too long.'
        break
      default:
        errMsg = 'There has been an unexpected error. Please try again. If the error persists, please contact help@dealvestors.com'
        break
    }
    return errMsg
  }

  handleInputChange(event) {
    const target = event.target
    const name = target.name
    const value = target.value

    this.setState(state => ({
      [name]: value,
      errors: { ...state.errors, [name]: this.validateField(name, value) },
    }))
  }

  validateField = (name, value) => {
    if (!value) {
      return 'is missing'
    }

    switch (name) {
      case 'firstName':
      case 'lastName': {
        if ('' === value.trim()) {
          return 'is invalid'
        }
        break
      }
      case 'email': {
        if (null === value.match(EMAIL_REGEX, '')) {
          return 'is invalid'
        }
        break
      }
      case 'phone': {
        if (!value.match(PHONE_REGEX) || value.length < 9) {
          return 'is invalid'
        }
        break
      }
      case 'message': {
        if ('' === value.trim()) {
          return 'is invalid'
        }
        break
      }
      default:
        break
    }
  }

  isValid = () => {
    return formFields.reduce((a, f) => a && this.state[f] && !this.state.errors[f], true)
  }

  loadingState = () => {
    this.setState(prevState => ({
      loading: !prevState.loading,
    }))
  }

  render() {
    const { classes, handleClose, showCloseButton } = this.props
    const { errors, loading, loggedIn } = this.state

    const textFields = [
      ['firstName', { placeholder: 'First Name', disabled: loggedIn }, {}, { maxLength: 40 }],
      ['lastName', { placeholder: 'Last Name', disabled: loggedIn }, {}, { maxLength: 80 }],
      ['phone', { type: 'phone', placeholder: 'Phone Number', disabled: loggedIn }, {}, { maxLength: 40 }],
      ['email', { placeholder: 'Email', disabled: loggedIn }, {}, { maxLength: 80 }],
      ['message', { multiline: true, rows: 10 }, { inputClass: `${classes.inputElement} ${classes.multiline}` }, { maxLength: 32000 }],
    ]

    const commonProps = (inputClass = classes.inputElement) => ({
      InputProps: {
        disableUnderline: true,
        classes: {
          root: classes.bootstrapRoot,
          input: inputClass,
        },
      },
      InputLabelProps: {
        shrink: true,
        className: classes.bootstrapFormLabel,
      },
    })

    return (
      <Grid>
        <div className={classes.requestFormDesktop}>
          <div className={classes.requestForm}>
            {showCloseButton && (
              <a className={classes.closeLink} onClick={handleClose}>
                Close
              </a>
            )}
            {textFields.map(([name, props, options, inputProps]) => (
              <TextField
                autoComplete="off"
                key={name}
                className={classes.formField}
                name={name}
                value={this.state[name]}
                error={errors[name] ? true : false}
                onChange={this.handleInputChange}
                helperText={errors[name]}
                inputProps={inputProps}
                {...props}
                {...commonProps(options && options.inputClass)}
              />
            ))}
          </div>
          <Button onClick={this.handleSubmit} className={classes.requestInfoButton} disabled={!this.isValid()}>
            {loading ? <Loader color="white" /> : 'Request Info'}
          </Button>
        </div>
      </Grid>
    )
  }
}

RequestForm.propTypes = {
  propertyID: PropTypes.number.isRequired,
  user: PropTypes.object,
}

function mapStateToProps(state) {
  return {
    user: state.user,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      showFlashMessage,
      showFlashMessageOnUnexpectedError,
    },
    dispatch
  )
}

export default withStyles(styles)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(RequestForm)
)



// WEBPACK FOOTER //
// ./src/components/RequestForm.js